
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { mapActions } from 'vuex';
import { PlatformService, UserService, UserServiceProduct } from '@/services/merchantSpace/types';
import UserServiceDialog from '@/components/Dialogs/UserServiceDialog.vue';

@Component({
  components: {
    UserServiceDialog,
  },
  methods: {
    ...mapActions('notifications', ['addDanger']),
    ...mapActions('notifications', ['addInfo']),
  },
})
export default class DashboardUserService extends Vue {
  isLoading: boolean = false;

  platformServices: PlatformService[] = this.$store.getters['platform/getSortedPlatformServices'];

  userServices: UserService[] = this.$store.getters['user/getUserServices'];

  addDanger!: Function;

  addInfo!: Function;

  isUuid(value: string | null): boolean {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  getState(us: any): 'pendingvalidation' | 'valid' | 'notvalid' | 'fallback' {
    if (!us) return 'fallback';

    const { revoked, validated, revokedAt, validatedAt } = us;
    switch (true) {
      case !validated && !validatedAt && !revoked && !revokedAt:
        return 'pendingvalidation';
      case validated && validatedAt && !revoked && !revokedAt:
        return 'valid';
      default:
        return 'notvalid';
    }
  }

  getTextByState(us: any) {
    switch (this.getState(us)) {
      case 'pendingvalidation':
        return this.$t('userServicesInfo.pending');
      case 'valid':
        return this.$t('userServicesInfo.validated');
      case 'notvalid':
        return this.$t('userServicesInfo.adminInvalidate');
      case 'fallback':
        return this.$t('userServicesInfo.notAssociated');
      default:
        return this.$t('userServicesInfo.pending');
    }
  }

  getIcon(us: any) {
    const colors = {
      pendingvalidation: 'warning',
      notvalid: 'error',
      valid: 'success',
      fallback: 'info',
    };

    return colors[this.getState(us)] || colors.fallback;
  }

  getStatusIcon(us: any): string {
    const icons = {
      pendingvalidation: 'mdi-alert',
      notvalid: 'mdi-close-circle',
      valid: 'mdi-check-circle',
      fallback: 'mdi-help-circle',
    };

    return icons[this.getState(us)] || icons.fallback;
  }

  activeUserService(item: PlatformService): UserService | null {
    return this.getActiveUserService(this.getUserServiceByServiceId(item.serviceId));
  }

  dataToDisplay(platformService: PlatformService) {
    const userServices = this.getUserServiceByServiceId(platformService.serviceId);
    const activeUserService = this.getActiveUserService(userServices) || null;

    if (!activeUserService) return '';

    if (platformService.isVisibleUSP) {
      const activeUserServiceProduct = this.getActiveUserServiceProduct(activeUserService);
      const code = activeUserServiceProduct ? activeUserServiceProduct.productCode : null;
      if (this.isUuid(code)) return '';
      return this.getCodeToDisplay(activeUserServiceProduct ? activeUserServiceProduct.productCode : null, activeUserService);
    }

    if (platformService.isVisibleUS) {
      const code = activeUserService ? activeUserService.externalAccountId : null;
      if (this.isUuid(code)) return '';
      return this.getCodeToDisplay(activeUserService ? activeUserService.externalAccountId : null, activeUserService);
    }

    return '';
  }

  getActiveUserService(userServices: (UserService | null)[]): UserService | null {
    return userServices.find((us) => us && !us.revoked) || null;
  }

  getUserServiceByServiceId(serviceId: number): (UserService | null)[] {
    return this.userServices
      .map((userService: UserService) => {
        if (serviceId === userService.serviceId) {
          return userService;
        }
        return null;
      })
      .filter((us) => us !== null) as UserService[];
  }

  getActiveUserServiceProduct(userService: UserService | null): UserServiceProduct | null {
    if (!userService) return null;

    const activeUserServiceProduct = userService.userServiceProducts.find(({ revoked }) => !revoked);
    return activeUserServiceProduct || null;
  }

  getCodeToDisplay(code: string | null, userService: UserService): string {
    if (!code) {
      return 'Carte révoqué';
    }

    if (userService.service.emailAsExternal) {
      return userService.externalAccountEmail;
    }
    return code;
  }

  haveVisibleService(): boolean {
    if (!this.$store.getters['user/getConfirmationEmail']) {
      return false;
    }

    return this.platformServices.some((ps) => ps.isVisibleUS || ps.isVisibleUSP);
  }

  refreshComponent() {
    this.platformServices = this.$store.getters['platform/getSortedPlatformServices'];
    this.userServices = this.$store.getters['user/getUserServices'];

    const message = 'Le service a été ajouté avec succès';
    this.addInfo({ message });
  }

  showError() {
    const message = "Une erreur est survenue lors de l'enregistrement du service";
    this.addDanger({ message });
  }
}
